import { Button } from "primereact/button";
import "./subscription-summary.scss";
import { useTransactionStepContext } from "../../transaction/new-transaction/transaction-step.context";
import ParentInfo, { ParentInfoMember } from "@core/ui/parent-info/parent-info";
import { Divider } from "primereact/divider";
import { useNewSubscriptionContext } from "../new-subscription.context";
import { useCallback } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import ServiceInfo from "@core/ui/service-info/service-info";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import TextBlock from "@shared/ui/text-block/text-block";
import { MemberFormMap } from "@core/model/member-form-map";
import { MemberFormSchema } from "@factory/form/form.rule";

export interface SubscriptionSummaryProps {}

export function SubscriptionSummary() {
  const {
    memberInfo,
    isCreating,
    handlePrint,
    createSubscriptionFromServices,
    subscriptionStartDate,
  } = useNewSubscriptionContext();

  const { memberFormData, stepperRef, serviceFormData, calculateTransaction } =
    useTransactionStepContext();

  const { formatDate } = useDateTimeContext();

  const handleCreateSubscription = async (isPaid: boolean) => {
    createSubscriptionFromServices(isPaid);
  };

  const getServiceData = useCallback(() => {
    const serviceFullData = serviceFormData.map((item) => ({
      ...item,
      serviceData: {
        ...item.serviceData,
        service_name: item.serviceData.name,
        service_description: item.serviceData.description,
        date: item.date,
        time: item.time,
        created_at: subscriptionStartDate || new Date().toISOString(),
      },
    }));
    return serviceFullData;
  }, [serviceFormData, subscriptionStartDate]);

  const subscriptionTotal = useCallback(() => {
    return serviceFormData.reduce(
      (total, item) => total + (item.amount ?? 0),
      0
    );
  }, [serviceFormData]);

  // RENDER
  const summaryElement = (
    <section>
      <div className="flex flex-col justify-center items-center">
        <h5>
          Subscription Start Date:{" "}
          {subscriptionStartDate
            ? formatDate(subscriptionStartDate)
            : "Not selected"}
        </h5>
        <h5>Created: {formatDate(new Date())}</h5>
        <h4>
          Total Subscription Amount:{" "}
          <b>{currencyFormat(subscriptionTotal())}</b>
        </h4>
        <small className="text-center text-gray-600 mt-2">
          This is a recurring subscription that will automatically renew based
          on your service duration
        </small>
      </div>
      <Divider />

      <div className="flex justify-center">
        {memberInfo ? (
          <ParentInfoMember
            model={MemberFormMap.Map(memberInfo) as MemberFormSchema}
          />
        ) : (
          <ParentInfoMember model={memberFormData as MemberFormSchema} />
        )}
      </div>

      <small className="block">Subscription Services</small>
      <Divider />
      <section className="flex justify-center items-center gap-4 flex-wrap mb-4">
        <Accordion multiple activeIndex={[0]} className="w-full md:w-3/4">
          {getServiceData().map((item, id) => (
            <AccordionTab
              key={id}
              header={`Service ${id + 1}: ${
                item.serviceData?.service_name || "Service Information"
              }`}
            >
              {item.serviceData && <ServiceInfo model={item.serviceData} />}
            </AccordionTab>
          ))}
        </Accordion>
      </section>

      {/* No addons section for subscription */}
    </section>
  );

  return (
    <div id="SubscriptionSummary" className="subscription-summary">
      <section className="flex flex-col h-12rem">
        <div className="flex flex-col items-center border-2 border-dashed p-6">
          <h2 className="font-bold my-4">Subscription Summary</h2>
          {summaryElement}
        </div>
      </section>

      <section className="flex pt-4 justify-between">
        <Button
          label="Back"
          severity="secondary"
          icon="pi pi-arrow-left"
          onClick={() => (stepperRef as any).current.prevCallback()}
        />

        <section className="flex gap-2">
          <Button
            label="Print"
            disabled={isCreating}
            onClick={() => handlePrint()}
            outlined
            severity="secondary"
          />
          <Button
            label="Create Subscription"
            disabled={isCreating}
            onClick={() => handleCreateSubscription(false)}
          />
          <Button
            label="Create & Pay Subscription"
            disabled={isCreating}
            onClick={() => handleCreateSubscription(true)}
          />
        </section>
      </section>
    </div>
  );
}

export default SubscriptionSummary;
